/**
 * SectorBody (Task #64) — the sector detail side panel (operator-cop-doctrine
 * §4.3, §3B.3 composition). Header (name + lead + open/closed badge) → spatial
 * rollup of what's geographically inside the polygon (anomalies / response
 * resources / cameras / communities, the same idiom as R.3 nearest) → actions
 * (Edit Metadata · Edit Geometry · Close Sector).
 *
 * Free-text lead label per §6.6 (no auth yet). Close is terminal (soft-close).
 */
import { useEffect, useState } from 'react';
import type { Sector, SectorDetail } from '@tsr/shared-types';
import { closeSector, fetchSectorDetail, updateSectorMetadata } from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { sectorStyle } from './styles/sectorStyles';

interface RollupItem {
  id: string;
  label: string;
  kicker: string | null;
}

/** One titled rollup section (count + a short list). */
function RollupSection({
  title,
  count,
  items,
  testid,
}: {
  title: string;
  count: number;
  items: RollupItem[];
  testid: string;
}): JSX.Element {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {title}
        </span>
        <span className="text-[11px] text-slate-300" data-testid={`${testid}-count`}>
          {count}
        </span>
      </div>
      {items.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {items.slice(0, 12).map((it) => (
            <li key={it.id} className="text-[11px] text-slate-300 flex justify-between gap-2">
              <span className="truncate">{it.label}</span>
              {it.kicker && <span className="text-slate-500 shrink-0">{it.kicker}</span>}
            </li>
          ))}
          {items.length > 12 && (
            <li className="text-[10px] text-slate-500">+{items.length - 12} more…</li>
          )}
        </ul>
      ) : (
        <p className="mt-0.5 text-[11px] text-slate-500">None inside.</p>
      )}
    </div>
  );
}

export function SectorBody({
  sector,
  onChanged,
  onEditGeometry,
}: {
  sector: Sector;
  /** Re-fetch sectors layer + refresh after a metadata/close mutation. The
   *  updated row (when available) lets the parent keep the panel in sync. */
  onChanged?: (updated?: Sector) => void;
  /** Start the draw tool to redraw THIS sector's polygon (App wires the PATCH). */
  onEditGeometry?: (sector: Sector) => void;
}): JSX.Element {
  const [detail, setDetail] = useState<SectorDetail | null>(null);
  const [closed, setClosed] = useState(Boolean(sector.closed_at));
  const [closing, setClosing] = useState(false);
  // Inline metadata edit form.
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sector.name);
  const [lead, setLead] = useState(sector.lead_label ?? '');
  const [notes, setNotes] = useState(sector.notes ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    const ctl = new AbortController();
    setDetail(null);
    fetchSectorDetail(sector.id, ctl.signal)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch((err) => {
        if ((err as { name?: string }).name !== 'AbortError') console.error('fetchSectorDetail', err);
      });
    // Reset the edit form when the selected sector changes.
    setName(sector.name);
    setLead(sector.lead_label ?? '');
    setNotes(sector.notes ?? '');
    setEditing(false);
    setClosed(Boolean(sector.closed_at));
    return () => {
      alive = false;
      ctl.abort();
    };
  }, [sector.id, sector.name, sector.lead_label, sector.notes, sector.closed_at]);

  const style = sectorStyle(closed ? 'closed' : null);

  const saveMetadata = (): void => {
    if (saving) return;
    setSaving(true);
    void updateSectorMetadata(sector.id, {
      session_id: getSessionId(),
      name: name.trim() || undefined,
      lead_label: lead.trim() === '' ? null : lead.trim(),
      notes: notes.trim() === '' ? null : notes.trim(),
    })
      .then((updated) => {
        showToast('Sector updated.', { variant: 'success' });
        setEditing(false);
        onChanged?.(updated);
      })
      .catch((err) => showToast((err as Error).message || 'Could not update sector.', { variant: 'error' }))
      .finally(() => setSaving(false));
  };

  const close = (): void => {
    if (closing) return;
    setClosing(true);
    void closeSector(sector.id, { session_id: getSessionId() })
      .then((updated) => {
        setClosed(true);
        showToast(`Sector "${sector.name}" closed.`, { variant: 'success' });
        onChanged?.(updated);
      })
      .catch((err) => showToast((err as Error).message || 'Could not close sector.', { variant: 'error' }))
      .finally(() => setClosing(false));
  };

  const r = detail?.rollup;

  return (
    <div className="px-4 py-3 space-y-3 text-sm" data-testid="sector-body">
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-base font-semibold text-amber-200" data-testid="sector-name">
            {sector.name}
          </div>
          <span
            data-testid="sector-status-badge"
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded border"
            style={{ color: style.color, borderColor: style.color }}
          >
            {style.label}
          </span>
        </div>
        <div className="text-[11px] text-slate-400">
          Sector{sector.incident_number ? ` · Incident ${sector.incident_number}` : ''}
          {sector.lead_label ? ` · ${sector.lead_label}` : ''}
        </div>
      </div>

      {/* Spatial rollup (§3B.3 — what's geographically inside the polygon). */}
      {!r ? (
        <p className="text-[11px] text-slate-500">Loading rollup…</p>
      ) : (
        <div className="space-y-2.5 border-t border-slate-700/60 pt-2">
          <RollupSection
            title="Anomalies inside"
            count={r.anomalies.total}
            testid="sector-anomalies"
            items={[
              ...r.anomalies.high_risk_hotspots,
              ...r.anomalies.smoke_detections,
              ...r.anomalies.cap_alerts,
            ]}
          />
          <div data-testid="sector-resources">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Response resources inside
              </span>
              <span className="text-[11px] text-slate-300" data-testid="sector-resources-count">
                {r.response_assets.total}
              </span>
            </div>
            {Object.keys(r.response_assets.by_class).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {Object.entries(r.response_assets.by_class).map(([cls, n]) => (
                  <span
                    key={cls}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700"
                  >
                    {cls.replace(/_/g, ' ')} · {n}
                  </span>
                ))}
              </div>
            )}
            {r.response_assets.items.length === 0 && (
              <p className="mt-0.5 text-[11px] text-slate-500">None inside.</p>
            )}
          </div>
          <RollupSection
            title="Cameras inside"
            count={r.cameras.total}
            testid="sector-cameras"
            items={r.cameras.items}
          />
          <RollupSection
            title="Communities inside"
            count={r.communities.total}
            testid="sector-communities"
            items={r.communities.items}
          />
        </div>
      )}

      {/* Actions. */}
      {!closed && (
        <div className="border-t border-slate-700/60 pt-2 space-y-2">
          {editing ? (
            <div className="space-y-1.5" data-testid="sector-edit-form">
              <input
                data-testid="sector-edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sector name"
                className="w-full text-[12px] px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100"
              />
              <input
                data-testid="sector-edit-lead"
                value={lead}
                onChange={(e) => setLead(e.target.value)}
                placeholder="Sector Lead (free text)"
                className="w-full text-[12px] px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100"
              />
              <textarea
                data-testid="sector-edit-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes"
                rows={2}
                className="w-full text-[12px] px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="sector-edit-save"
                  disabled={saving}
                  onClick={saveMetadata}
                  className="text-[11px] py-1 px-2 rounded bg-sky-700/70 hover:bg-sky-700 border border-sky-600 text-sky-100 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="text-[11px] py-1 px-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="sector-edit-btn"
                onClick={() => setEditing(true)}
                className="text-[11px] py-1 px-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
              >
                Edit Metadata
              </button>
              <button
                type="button"
                data-testid="sector-edit-geometry-btn"
                onClick={() => onEditGeometry?.(sector)}
                className="text-[11px] py-1 px-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
              >
                Edit Geometry
              </button>
              <button
                type="button"
                data-testid="sector-close-btn"
                disabled={closing}
                onClick={close}
                className="text-[11px] py-1 px-2 rounded bg-rose-700/60 hover:bg-rose-700 border border-rose-600 text-rose-100 disabled:opacity-50"
              >
                Close Sector
              </button>
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-slate-500 leading-relaxed">
        Operator-drawn sector (IMS §5.3 geographic sector). The rollup auto-filters the
        anomalies, resources, cameras + communities inside the polygon. Lead is a free-text label
        (role-based assignment lands with auth).
      </p>
    </div>
  );
}
